<!-- eslint-disable vue/no-v-html -->
<template>
  <main>
    <div class="d-flex align-center justify-space-between mb-5">
      <div class="d-flex align-center">
        <v-btn to="/" icon class="mr-2" title="Back to page">
          <v-icon>far fa-arrow-left</v-icon>
        </v-btn>
        <h1>Debug Page</h1>
      </div>
      <div class="d-flex align-center">
        <div class="d-flex align-center">
          <span class="mr-2">Devtools:</span>
          <v-switch v-model="devtools" dense color="primary"></v-switch>
        </div>
        <div class="d-flex align-center">
          <span class="mr-2">Formatted:</span>
          <v-switch v-model="formatted" dense color="primary"></v-switch>
        </div>
      </div>
    </div>

    <template v-if="!connected">
      Websocket server not connected ...
    </template>

    <template v-else>
      <h2 class="mb-5">Active Rooms ({{rooms.length}}) / Peers ({{totalPeers}})</h2>

      <v-expansion-panels v-if="formatted" class="mb-5" accordion multiple focusable>
        <v-expansion-panel v-for="(room) in rooms" :key="room.id">
          <v-expansion-panel-header>
            <div>
              <template v-if="room.id.startsWith('local:')">
                <strong>{{room.id.substring(6)}}</strong>
              </template>
              <template v-else>
                <strong>{{room.name}}</strong> <small>({{room.id}})</small>
              </template>
              - {{room.peers.length}} Peer<template v-if="room.peers.length !== 1">s</template>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            <h4 class="mb-4">Details:</h4>
            <v-simple-table>
              <template #default>
                <tbody>
                  <tr v-for="key in Object.keys(roomData(room))" :key="key">
                    <td>{{key}}</td>
                    <td>{{room[key] || '-'}}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <h4 class="mt-4 mb-4">Peers: ({{room.peers.length}})</h4>
            <v-expansion-panels accordion multiple focusable>
              <v-expansion-panel v-for="(peer) in room.peers" :key="peer.id">
                <v-expansion-panel-header>
                  <div>
                    {{getUserName(peer)}} <small>({{getDeviceName(peer)}})</small>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pt-4">
                  <v-simple-table>
                    <template #default>
                      <tbody>
                        <tr v-for="key in Object.keys(roomData(peer))" :key="key">
                          <td>{{key}}</td>
                          <td>{{peer[key] || '-'}}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <pre v-else class="mb-5">{{JSON.stringify(rooms, null, 2)}}</pre>

      <h2 class="mb-5">Connected Sockets ({{sockets.length}})</h2>
      <v-expansion-panels v-if="formatted" class="mb-5" accordion multiple focusable>
        <v-expansion-panel v-for="(socket) in sockets" :key="socket.id">
          <v-expansion-panel-header>
            <div>
              <strong>{{socket.id}}</strong>
              - {{socket.rooms.length}} Room<template v-if="socket.rooms.length !== 1">s</template>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            <h4 class="mb-4">Rooms: ({{socket.rooms.length}})</h4>
            <div v-html="socket.rooms.join('<br>')"></div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <pre v-else class="mb-5">{{JSON.stringify(sockets, null, 2)}}</pre>
    </template>

    <h2 class="mb-5">Various Data:</h2>
    <pre class="mb-5">{{JSON.stringify(variousData, null, 2)}}</pre>
  </main>
</template>

<script>
  /* global TransformStream */
  import io from 'socket.io-client/dist/socket.io.slim';
  import {settings, setSettings} from 'xca-app-core/src/utils/settings';
  import {consoleLog as _consoleLog, consoleError as _consoleError} from 'xca-app-core/src/utils/console';
  import {getUserName, getDeviceName} from '../components/peers/peer-utils';


  const consoleLog = (...args) => _consoleLog('[DEBUG]', ...args);


  const consoleError = (...args) => _consoleError('[DEBUG]', ...args);


  export default {
    name: 'Debug',

    data() {
      return {
        connected: false,
        formatted: true,
        documentTitle: 'Debug',
        rooms: [],
        sockets: [],
        variousData: {}
      };
    },

    computed: {
      devtools: {
        get() {
          return settings.devtools;
        },
        set(devtools) {
          setSettings({devtools});
          localStorage.setItem('xca-app-core_devtools', devtools);
        }
      },
      totalPeers() {
        let count = 0;
        for (const room of this.rooms) {
          count += room.peers.length;
        }
        return count;
      }
    },

    created() {
      // Check streamsaver featues.

      let useBlobFallback = /constructor/i.test(window.HTMLElement) || !!window.safari || !!window.WebKitPoint;
      try {
        // We can't look for service worker since it may still work on http
        new Response(new ReadableStream());
        if (isSecureContext && !('serviceWorker' in navigator)) {
          useBlobFallback = true;
        }
      } catch (err) {
        useBlobFallback = true;
      }

      let supportsTransferable = false;
      try {
        // Transfariable stream was first enabled in chrome v73 behind a flag
        const {readable} = new TransformStream();
        const mc = new MessageChannel();
        mc.port1.postMessage(readable, [readable]);
        mc.port1.close();
        mc.port2.close();
        supportsTransferable = true;

      } catch (error) {
        /* NOOP */
      }

      this.variousData.streamSaver = {
        isSecureContext,
        serviceWorker: 'serviceWorker' in navigator,
        useBlobFallback,
        supportsTransferable,
        downloadStrategy: isSecureContext || 'MozAppearance' in document.documentElement.style ? 'iframe' : 'navigate'
      };
    },

    mounted() {
      const socket = this.socket = io('/debug', {transports: ['websocket']});

      socket.on('connect', () => {
        consoleLog('connect');
        this.connected = true;
      });

      socket.on('disconnect', (reason) => {
        consoleLog('disconnect', reason);
        this.connected = false;
        if (reason === 'io server disconnect') {
          // the disconnection was initiated by the server, you need to reconnect manually
          socket.connect();
        }
      });

      socket.on('reconnect', () => {
        consoleLog('reconnect');
        this.connected = true;
      });

      socket.on('reconnect_error', () => consoleLog('reconnect_error'));

      socket.on('error', (error) => consoleError(error));

      socket.on('debug', ({rooms, sockets}) => {
        this.rooms = rooms;
        this.sockets = sockets;
      });
    },

    beforeDestroy() {
      if (this.socket) {
        this.socket.disconnect();
      }
    },

    methods: {
      getUserName(peer) {
        return getUserName(peer);
      },

      getDeviceName(peer) {
        return getDeviceName(peer);
      },

      roomData(room) {
        room = {...room};
        delete room.peers;
        delete room.name_lowercase;
        return room;
      }
    }
  };
</script>

<style lang="scss" scoped>
  h1,
  h2,
  h3,
  h4,
  strong {
    font-weight: 500;
  }

  main {
    padding: 10px 20px;
  }

  pre {
    background: #fff;
    padding: 16px 24px;
    border-radius: 4px;
    box-shadow:
      0 3px 1px -2px rgb(0 0 0 / 20%),
      0 2px 2px 0 rgb(0 0 0 / 14%),
      0 1px 5px 0 rgb(0 0 0 / 12%);
  }
</style>
